import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './AgentDetails.css';
import api from '../../api/axios.js';
import { API_IMAGE_BASE_URL } from '../../config/api';
import { FiArrowLeft, FiEdit, FiPhone, FiMail, FiMapPin, FiUser, FiTarget, FiSearch, FiEye, FiPlus, FiX, FiFileText } from "react-icons/fi";
import OfferLetterModal from '../../components/OfferLetter/OfferLetterModal';

const AgentDetails = () => {
  console.log('AgentDetails component loaded'); // Debug log
  const { id } = useParams();
  console.log('Agent ID from params:', id); // Debug log
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [blockActionLoading, setBlockActionLoading] = useState(false);
  const [listData, setListData] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listPagination, setListPagination] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [done,setDone]=useState(false);

  // Assignment modal states
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignmentData, setAssignmentData] = useState([]);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentSearch, setAssignmentSearch] = useState('');
  const [assignmentPagination, setAssignmentPagination] = useState({});

  // Target filter states
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Target and Edit modal states
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showOfferLetterModal, setShowOfferLetterModal] = useState(false);
  const [targetLoading, setTargetLoading] = useState(false);

  // Target form data
  const [targetForm, setTargetForm] = useState({
    target: '',
    month: '',
    year: new Date().getFullYear(),
    startDate: '',
    endDate: ''
  });

  // Edit form data
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    mobile_number: ''
  });
  const [formErrors, setFormErrors] = useState({});

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  useEffect(() => {
   fetchAgentDetails();
  }, [id, selectedMonth, selectedYear,done]);

  useEffect(() => {
    if (agent) {
      fetchListData();
    }
  }, [agent, searchTerm]);



  const handleApproveReject = async (approvalStatus) => {
    try {
      setActionLoading(true);
      await api.put(`/admin/approve-advisor/${id}`, {
        approval_status: approvalStatus
      });

      toast.success(`Agent ${approvalStatus ? 'approved' : 'rejected'} successfully!`);
      await fetchAgentDetails();
    } catch (error) {
      console.error('Approval/Rejection error:', error);
      toast.error('Failed to update agent status');
    } finally {
      setActionLoading(false);
    }
  };

  const isAdvisorBlocked = ["inactive", "blocked"].includes(
    (agent?.status || "").toLowerCase()
  );

  const handleToggleBlockStatus = async () => {
    try {
      setBlockActionLoading(true);
      if (isAdvisorBlocked) {
        await api.put(`/admin/reactivate/user/${id}`);
        toast.success("Advisor unblocked successfully");
      } else {
        await api.put(`/admin/soft-delete/user/${id}`, {
          reason: "inactive",
        });
        toast.success("Advisor blocked successfully");
      }
      await fetchAgentDetails();
    } catch (error) {
      console.error("Advisor block/unblock error:", error);
      toast.error(error?.response?.data?.message || "Failed to update advisor status");
    } finally {
      setBlockActionLoading(false);
    }
  };

  const getAssignmentEndpoint = () => {
    const advisorRole = agent.roles?.find(role =>
      ['advisor-district-coordinator', 'advisor-commisionerate', 'advisor-state-head'].includes(role.userType)
    );

    if (advisorRole?.userType === 'advisor-state-head') {
      return '/advisor/commissionerates';
    } else if (advisorRole?.userType === 'advisor-commisionerate') {
      return '/advisor/district-coordinators';
    } else if (advisorRole?.userType === 'advisor-district-coordinator') {
      return '/advisor/vendors';
    }
    return null;
  };

  const getAssignmentTitle = () => {
    const advisorRole = agent.roles?.find(role =>
      ['advisor-district-coordinator', 'advisor-commisionerate', 'advisor-state-head'].includes(role.userType)
    );

    if (advisorRole?.userType === 'advisor-state-head') {
      return 'Assign Commissionery Manager';
    } else if (advisorRole?.userType === 'advisor-commisionerate') {
      return 'Assign District Coordinator';
    } else if (advisorRole?.userType === 'advisor-district-coordinator') {
      return 'Assign Vendor';
    }
    return 'Assign';
  };

  const fetchAssignmentData = async (page = 1) => {
    const endpoint = getAssignmentEndpoint();
    if (!endpoint) return;

    try {
      setAssignmentLoading(true);
      const res = await api.get(endpoint, {
        params: {
          search: assignmentSearch,
          page,
          limit: 10
        }
      });

      setAssignmentData(res.data.data || []);
      setAssignmentPagination({
        page: res.data.page || 1,
        limit: res.data.limit || 10,
        total: res.data.total || 0,
        totalPages: res.data.totalPages || 1
      });
    } catch (error) {
      console.error('Fetch assignment data error:', error);
      toast.error('Failed to fetch assignment data');
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleTargetAssignment = async () => {
    try {
      setTargetLoading(true);
      let endpoint = '';
      let body = {};

      const advisorRole = agent.roles?.find(role =>
        ['advisor-district-coordinator', 'advisor-commisionerate', 'advisor-state-head'].includes(role.userType)
      );

      if (advisorRole?.userType === 'advisor-state-head') {
        endpoint = '/admin/target/state-head';
        body = {
          stateHeadId: agent._id,
          target: parseInt(targetForm.target),
          month: targetForm.month,
          startDate: targetForm.startDate,
          endDate: targetForm.endDate,
          year: targetForm.year
        };
      } else if (advisorRole?.userType === 'advisor-commisionerate') {
        endpoint = '/admin/target/commissionerate';
        body = {
          stateHeadId: agent._id,
          distributions: [{
            target: parseInt(targetForm.target),
            commissionerateId: agent._id
          }],
          month: targetForm.month,
          startDate: targetForm.startDate,
          endDate: targetForm.endDate,
          year: targetForm.year
        };
      } else if (advisorRole?.userType === 'advisor-district-coordinator') {
        endpoint = '/admin/target/district';
        body = {
          commissionerateId: agent._id,
          distributions: [{
            target: parseInt(targetForm.target),
            districtCoordinatorId: agent._id
          }],
          month: targetForm.month,
          startDate: targetForm.startDate,
          endDate: targetForm.endDate,
          year: targetForm.year
        };
      }

      await api.post(endpoint, body);
      toast.success('Target assigned successfully!');
      setShowTargetModal(false);
      setTimeout(() => {
        fetchAgentDetails();
      }, 2000);
      
    } catch (error) {
      console.error('Target assignment error:', error);
      toast.error('Failed to assign target');
    } finally {
      setTargetLoading(false);
    }
  };

  const validateName = (name) => {
  if (!name.trim()) return "Name is required";

  if (name.trim().length < 2)
    return "Name must be at least 2 characters";

  if (name.trim().length > 50)
    return "Name cannot exceed 50 characters";

  if (!/^[A-Za-z\s]+$/.test(name))
    return "Name should contain only letters";

  return "";
};

const validateEmail = (email) => {
  if (!email.trim()) return "Email is required";

  if (!/^\S+@\S+\.\S+$/.test(email))
    return "Invalid email format";

  return "";
};

const validateMobile = (mobile) => {
  if (!mobile.trim())
    return "Mobile number is required";

  if (!/^[6-9]\d{9}$/.test(mobile))
    return "Enter a valid 10-digit mobile number";

  return "";
};

const validateSalary = (salary) => {
  if (!salary) return "Salary is required";

  if (Number(salary) <= 0)
    return "Salary must be greater than 0";

  return "";
};
const handleEditAgent = async () => {
  const errors = {};

  const nameError = validateName(editForm.name);
  if (nameError) errors.name = nameError;

  const emailError = validateEmail(editForm.email);
  if (emailError) errors.email = emailError;

  const mobileError = validateMobile(editForm.mobile_number);
  if (mobileError) errors.mobile_number = mobileError;

  if (Object.keys(errors).length > 0) {
    setFormErrors(errors);
    return;
  }

  try {
    await api.put(`/admin/update/user/${agent._id}`, {
      name: editForm.name.trim(),
      email: editForm.email.trim(),
      mobile_number: editForm.mobile_number.trim(),
    });

    toast.success("Advisor updated successfully!");
    setFormErrors({});
    setShowEditModal(false);
    fetchAgentDetails();

  } catch (error) {
    if (error?.response?.status === 409) {
      setFormErrors({
        email: "Email already exists"
      });
      return;
    }

    toast.error(
      error?.response?.data?.message ||
      "Failed to update advisor"
    );
  }
};

  const openTargetModal = () => {
    setTargetForm({
      target: '',
      month: '',
      year: new Date().getFullYear(),
      startDate: '',
      endDate: ''
    });
    setShowTargetModal(true);
  };

  const openEditModal = () => {
    setFormErrors({});
    setEditForm({
      name: agent.name,
      email: agent.email || '',
      mobile_number: agent.mobile_number || ''
    });
    setShowEditModal(true);
  };

  const handleAssignment = async (targetId) => {
    const advisorRole = agent.roles?.find(role =>
      ['advisor-district-coordinator', 'advisor-commisionerate', 'advisor-state-head'].includes(role.userType)
    );

    let endpoint = '';
    let body = {};

    if (advisorRole?.userType === 'advisor-state-head') {
      endpoint = '/admin/assignment/commissionerate';
      body = { stateHeadId: agent._id, commissionerateId: targetId };
    } else if (advisorRole?.userType === 'advisor-commisionerate') {
      endpoint = '/admin/assignment/district';
      body = { commissionerateId: agent._id, districtCoordinatorId: targetId };
    } else if (advisorRole?.userType === 'advisor-district-coordinator') {
      endpoint = '/admin/assignment/vendor';
      body = { districtCoordinatorId: agent._id, vendorId: targetId };
    }

    try {
      const response = await api.post(endpoint, body);
      toast.success(response.data.message || 'Assignment successful');
      setShowAssignModal(false);
       setTimeout(() => {
        fetchAgentDetails();
      }, 2000);

    } catch (error) {
      console.error('Assignment error:', error);
      toast.error(error.response?.data?.message || 'Failed to complete assignment');
    }
  };

  useEffect(() => {
    if (showAssignModal) {
      fetchAssignmentData();
    }
  }, [showAssignModal, assignmentSearch]);

  const fetchAgentDetails = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/user/profile`, {
        params: {
          user_id: id,
          Month: selectedMonth,
          Year: selectedYear
        }
      });
      setAgent(res.data.data || res.data);
    } catch (error) {
      console.error("Fetch agent details error:", error);
      setAgent(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchListData = async (page = 1) => {
    if (!agent) return;

    try {
      setListLoading(true);
      let endpoint = '';
      const params = { page, limit: 10, search: searchTerm };

      if (agent.userType === 'advisor-state-head') {
        endpoint = '/admin/commissionerates';
        params.state_head_id = agent._id;
      } else if (agent.userType === 'advisor-commisionerate') {
        endpoint = '/admin/district-coordinators';
        params.commissionerateId = agent._id;
      } else if (agent.userType === 'advisor-district-coordinator') {
        endpoint = '/admin/vendors';
        params.districtCoordinatorId = agent._id;
      }

      if (endpoint) {
        const res = await api.get(endpoint, { params });
        setListData(res.data.data || []);
        setListPagination({
          page: res.data.pagination?.page || res.data.page || 1,
          limit: res.data.pagination?.limit || res.data.limit || 10,
          total: res.data.pagination?.totalRecords || res.data.total || 0,
          totalPages: res.data.pagination?.totalPages || res.data.totalPages || 1
        });
      }
    } catch (error) {
      console.error("Fetch list data error:", error);
      setListData([]);
    } finally {
      setListLoading(false);
    }
  };

  const handleNextPage = () => {
    if (listPagination.page < listPagination.totalPages) {
      fetchListData(listPagination.page + 1);
    }
  };

  const handlePrevPage = () => {
    if (listPagination.page > 1) {
      fetchListData(listPagination.page - 1);
    }
  };

  const getListTitle = () => {
    if (agent?.userType === 'advisor-state-head') return 'Commissionery Manager';
    if (agent?.userType === 'advisor-commisionerate') return 'District Coordinators';
    if (agent?.userType === 'advisor-district-coordinator') return 'Vendors';
    return 'List';
  };

  const formatRoleName = (agent) => {
    // Check if user has advisor roles
    const advisorRole = agent.roles?.find(role =>
      role.userType.includes('advisor') ||
      ['advisor-district-coordinator', 'advisor-commisionerate', 'advisor-state-head'].includes(role.userType)
    );

    if (advisorRole) {
      const roleMap = {
        'advisor-district-coordinator': 'District Coordinator',
        'advisor-commisionerate': 'Commissionery Manager',
        'advisor-state-head': 'State Head'
      };
      return roleMap[advisorRole.userType] || advisorRole.userType;
    }

    // Fallback to userType
    const roleMap = {
      'advisor-district-coordinator': 'District Coordinator',
      'advisor-commisionerate': 'Commissionery Manager',
      'advisor-state-head': 'State Head',
      'customer': 'Customer',
      'vendor': 'Vendor'
    };
    return roleMap[agent.userType] || agent.userType;
  };

  const formatTarget = (target) => {
    if (!target) return { assigned: '₹ 0', achieved: '₹ 0' };

    console.log('target data:', target.achieved);

    return {
      assigned: `₹ ${target.assigned ?? 0}`,
      achieved: `₹ ${target.achieved ?? 0}`
    };
  };

  if (loading) {
    return (
      <div className="agent-details">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading agent details...</p>
        </div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="agent-details">
        <div className="error-container">
          <h2>Agent not found</h2>
          <p>The agent with ID {id} could not be found.</p>
          <button onClick={() => navigate('/agent-management')} className="back-btn">
            <FiArrowLeft /> Back to Agent Management
          </button>
        </div>
      </div>
    );
  }

  const targetData = formatTarget(agent?.target);



  return (
    <div className="agent-details">
      <div className="details-header">
        <div className="header-left">
          <button onClick={() => navigate('/agent-management')} className="back-btn">
            <FiArrowLeft /> Back
          </button>
        </div>
        <div className="header-actions">
          {agent.isApproved === 'pending' ? (
            <>
              <button
                className="reject-btn"
                onClick={() => handleApproveReject('rejected')}
                disabled={actionLoading}
              >
                {actionLoading ? 'Processing...' : 'Reject'}
              </button>
              <button
                className="approve-btn"
                onClick={() => handleApproveReject('approved')}
                disabled={actionLoading}
              >
                {actionLoading ? 'Processing...' : 'Approve'}
              </button>
            </>
          ) : (
            agent.isApproved === 'approved' ? (
              <div className="approval-status">
                <span className="approved-badge">✓ Approved</span>
              </div>
            ) : (
              <div className="approval-status">
                <span className="rejected-badge">✗ Rejected</span>
              </div>
            )
          )}

          <button
            className="edit-agent-btn"
            onClick={handleToggleBlockStatus}
            disabled={blockActionLoading}
          >
            {blockActionLoading
              ? "Please wait..."
              : isAdvisorBlocked
              ? "Unblock Advisor"
              : "Block Advisor"}
          </button>

          {getAssignmentEndpoint() && (
            <button
              className="assign-btn"
              onClick={() => setShowAssignModal(true)}
            >
              <FiPlus /> {getAssignmentTitle()}
            </button>
          )}
        </div>
      </div>

      {/* Month/Year Filter */}
      <div className="filter-section">
        <div className="filter-controls">
          <div className="filter-group">
            <label>Month:</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            >
              {months.map(month => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Year:</label>
            <input
              type="number"
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              min="2020"
              max="2030"
            />
          </div>
        </div>
      </div>

      <div className="agent-info-card">
        <div className="agent-avatar">
          <div className="avatar-circle">
            {agent.profile_image ? <img src={`${API_IMAGE_BASE_URL}${agent.profile_image}`} width={100} height={100}/> : agent.name?.charAt(0)?.toUpperCase()}

          </div>
        </div>

        <div className="agent-name">
          {/* <span className="label">Agent Name</span> */}
          <h2>
  {agent?.name
    ? agent.name.charAt(0).toUpperCase() + agent.name.slice(1)
    : 'N/A'}
</h2>
        </div>

        <div className="agent-details-grid">
          <div className="detail-item">
            <span className="label">Role</span>
            <span className="value">{formatRoleName(agent)}</span>
          </div>

          <div className="detail-item">
            <span className="label">Email</span>
            <span className="value">{agent.email}</span>
          </div>

         <div className="detail-item">
  <span className="label">Phone</span>
  <span className="value">
    {agent?.mobile_number?.trim() || 'N/A'}
  </span>
</div>

          <div className="detail-item">
            <span className="label">Gender</span>
            <span className="value">{agent.gender || 'Male'}</span>
          </div>
        </div>

        <div className="salary-section">
          <div className="target-grid">
            <div className="target-item assigned">
              <span className="target-label">Target Assigned</span>
              <span className="target-value">{targetData?.assigned}</span>
            </div>
            <div className="target-item achieved">
              <span className="target-label">Target Achieved</span>
              <span className="target-value">{targetData?.achieved}</span>
            </div>
          </div>
          <div className="target-actions">
            <button className="add-salary-btn" onClick={openTargetModal}>Update Target</button>
            <button className="edit-agent-btn" onClick={openEditModal}><FiEdit /> Edit Advisor</button>
            <button className="offer-letter-btn" onClick={() => setShowOfferLetterModal(true)}>
              <FiFileText /> Generate Offer Letter
            </button>
          </div>
        </div>

        <div className="offer-letter-section">
          <div className="offer-item">
            <span className="offer-label">Salary</span>
            <span className="offer-value">₹ {agent.salary?.toLocaleString() || 'Not Set'}</span>
          </div>
          {agent.offerLetter && (
            <div className="offer-item">
              <span className="offer-label">Offer Letter</span>
              <a href={`${API_IMAGE_BASE_URL}${agent.offerLetter}`} target="_blank" rel="noopener noreferrer" className="offer-file">View Offer Letter</a>
            </div>
          )}
        </div>
      </div>

      {/* Hierarchical List Section */}
      {agent && (
        <div className="list-section">
          <div className="list-header">
            <h3>📋 {getListTitle()}</h3>
            <div className="search-box">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <span className="search-icon"><FiSearch /></span>
            </div>
          </div>

          <div className="list-table">
            {listLoading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading {getListTitle().toLowerCase()}...</p>
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    {agent.userType !== 'advisor-district-coordinator' && <th>Target Assigned</th>}
                    {agent.userType !== 'advisor-district-coordinator' && <th>Target Achieved</th>}
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {listData.length > 0 ? (
                    listData.map((item, index) => {
                      const target = formatTarget(item.target);
                      return (
                        <tr key={item._id} className="table-row-animated" style={{ animationDelay: `${index * 0.1}s` }}>
<td>
  {item?.name
    ? item.name.charAt(0).toUpperCase() + item.name.slice(1)
    : 'N/A'}
</td>

<td>
  {item?.email?.trim() || 'N/A'}
</td>

<td>
  {item?.mobile_number
    ? String(item.mobile_number).trim()
    : 'N/A'}
</td>

{agent.userType !== 'advisor-district-coordinator' && (
  <td>{target?.assigned ?? '0'}</td>
)}

{agent.userType !== 'advisor-district-coordinator' && (
  <td>{target?.achieved ?? '0'}</td>
)}

<td>
  <span className={`status ${item?.status || ''}`}>
    {item?.status
      ? item.status.charAt(0).toUpperCase() + item.status.slice(1)
      : 'N/A'}
  </span>
</td>
                          <td>
                            <button
                              className="icon-btn view"
                              onClick={() => {
                                if (agent.userType === 'advisor-district-coordinator') {
                                  navigate(`/vendor-details/${item._id}`);
                                } else {
                                  navigate(`/agent-management/${item._id}`);
                                }
                              }}
                              title="View Details"
                            >
                              <FiEye />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={agent.userType !== 'advisor-district-coordinator' ? "7" : "5"} className="no-data">
                        No {getListTitle().toLowerCase()} found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            {/* Pagination */}
            {listPagination.totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={handlePrevPage}
                  className={listPagination.page === 1 ? "pagination-disabled" : "pagination-btn"}
                  disabled={listPagination.page === 1}
                >
                  Prev
                </button>
                <span>
                  Page {listPagination.page} of {listPagination.totalPages}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={listPagination.page === listPagination.totalPages}
                  className={
                    listPagination.page === listPagination.totalPages
                      ? "pagination-disabled"
                      : "pagination-btn"
                  }
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="additional-info">
        <div className="info-section">
          <h3>Personal Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="label">Date of Birth</span>
              <span className="value">
                {agent.dateOfBirth ? new Date(agent.dateOfBirth).toLocaleDateString() : 'N/A'}
              </span>
            </div>
            <div className="info-item">
              <span className="label">Gender</span>
              <span className="value">{agent.gender || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="label">Guardian Name</span>
              <span className="value">{agent.guardianName || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="label">Address</span>
              <span className="value">{agent.address || 'N/A'}</span>
            </div>
          </div>
        </div>

        <div className="info-section">
          <h3>Professional Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="label">Date of Joining</span>
              <span className="value">
                {agent.date_of_joining ? new Date(agent.date_of_joining).toLocaleDateString() : 'N/A'}
              </span>
            </div>
            <div className="info-item">
              <span className="label">Profile Completion</span>
              <span className="value">{agent.advisorProfileCompletion || 0}%</span>
            </div>
            <div className="info-item">
              <span className="label">Commissionery Manager</span>
              <span className="value">{agent.commissionerate || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="label">Approved</span>
              <span className={`value status ${agent.isApproved === 'approved' ? 'verified' : 'pending'}`}>
                {agent.isApproved === 'approved' ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
        </div>

        <div className="info-section">
          <h3>Documents</h3>
          <div className="documents-grid">
            <div className="document-item">
              <span className="doc-label">Aadhaar Card</span>
              {agent.adhaar_card_certificate ? (
                <a href={`${API_IMAGE_BASE_URL}${agent.adhaar_card_certificate}`} target="_blank" rel="noopener noreferrer" className="doc-link">View Document</a>
              ) : (
                <span className="doc-missing">Not Uploaded</span>
              )}
            </div>
            <div className="document-item">
              <span className="doc-label">PAN Card</span>
              {agent.pan_card_certificate ? (
                <a href={`${API_IMAGE_BASE_URL}${agent.pan_card_certificate}`} target="_blank" rel="noopener noreferrer" className="doc-link">View Document</a>
              ) : (
                <span className="doc-missing">Not Uploaded</span>
              )}
            </div>
            <div className="document-item">
              <span className="doc-label">GST Certificate</span>
              {agent.gst_certificate ? (
                <a href={`${API_IMAGE_BASE_URL}${agent.gst_certificate}`} target="_blank" rel="noopener noreferrer" className="doc-link">View Document</a>
              ) : (
                <span className="doc-missing">Not Uploaded</span>
              )}
            </div>
            {agent.document && agent.document.length > 0 && (
              <div className="document-item full-width">
                <span className="doc-label">Other Documents</span>
                <div className="doc-list">
                  {agent.document.map((doc, index) => (
                    <a key={doc._id} href={`${API_IMAGE_BASE_URL}${doc.url}`} target="_blank" rel="noopener noreferrer" className="doc-link">
                      Document {index + 1}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Assignment Modal */}
      {showAssignModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{getAssignmentTitle()}</h3>
              <button
                className="close-btn"
                onClick={() => setShowAssignModal(false)}
              >
                <FiX />
              </button>
            </div>

            <div className="modal-body">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search..."
                  value={assignmentSearch}
                  onChange={(e) => setAssignmentSearch(e.target.value)}
                />
                <span className="search-icon"><FiSearch /></span>
              </div>

              <div className="assignment-list">
                {assignmentLoading ? (
                  <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading...</p>
                  </div>
                ) : (
                  <>
                    {assignmentData.length > 0 ? (
                      assignmentData.map((item) => (
                        <div key={item._id} className="assignment-item">
                          <div className="item-info">
                            <h4>{item.name}</h4>
                            <p>{item.email}</p>
                            <span>{item.mobile_number}</span>
                          </div>
                          <button
                            className="assign-item-btn"
                            onClick={() => handleAssignment(item._id)}
                          >
                            Assign
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="no-data">Not available</div>
                    )}
                  </>
                )}
              </div>

              {assignmentPagination.totalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => fetchAssignmentData(assignmentPagination.page - 1)}
                    disabled={assignmentPagination.page === 1}
                  >
                    Prev
                  </button>
                  <span>
                    Page {assignmentPagination.page} of {assignmentPagination.totalPages}
                  </span>
                  <button
                    onClick={() => fetchAssignmentData(assignmentPagination.page + 1)}
                    disabled={assignmentPagination.page === assignmentPagination.totalPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Target Assignment Modal */}
      {showTargetModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Assign Target - {agent?.name}</h3>
              <button className="close-btn" onClick={() => setShowTargetModal(false)}>
                <FiX />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Target Amount</label>
                  <input
                    type="number"
                    value={targetForm.target}
                    onChange={(e) => setTargetForm({ ...targetForm, target: e.target.value })}
                    placeholder="Enter target amount"
                  />
                </div>

                <div className="form-group">
                  <label>Month</label>
                  <select
                    value={targetForm.month}
                    onChange={(e) => setTargetForm({ ...targetForm, month: parseInt(e.target.value) })}
                  >
                    <option value="">Select Month</option>
                    {months.map(month => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Year</label>
                  <input
                    type="number"
                    value={targetForm.year}
                    onChange={(e) => setTargetForm({ ...targetForm, year: parseInt(e.target.value) })}
                  />
                </div>

                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={targetForm.startDate}
                    onChange={(e) => setTargetForm({ ...targetForm, startDate: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={targetForm.endDate}
                    onChange={(e) => setTargetForm({ ...targetForm, endDate: e.target.value })}
                  />
                </div>

                 <div className="modal-actions">
                    <button
                      className="cancel-btn"
                      onClick={() => setShowTargetModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="assign-btn"
                      onClick={handleTargetAssignment}
                      disabled={targetLoading}
                    >
                      {targetLoading ? 'Assigning...' : 'Assign Target'}
                    </button>

                  </div>
               
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Agent Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit Advisor - {agent?.name}</h3>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>
                <FiX />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Name</label>
              <input
  type="text"
  value={editForm.name}
  onChange={(e) => {
    const value = e.target.value
      .replace(/[^A-Za-z\s]/g, '')
      .slice(0, 50);

    setEditForm({
      ...editForm,
      name: value
    });

    setFormErrors({
      ...formErrors,
      name: ''
    });
  }}
/>
                {formErrors.name && <span className="form-error">{formErrors.name}</span>}
              </div>

              <div className="form-group">
                <label>Email</label>
               <input
  type="email"
  value={editForm.email}
  onChange={(e) => {
    setEditForm({
      ...editForm,
      email: e.target.value.trim().slice(0, 100)
    });

    setFormErrors({
      ...formErrors,
      email: ''
    });
  }}
/>
                {formErrors.email && <span className="form-error">{formErrors.email}</span>}
              </div>

              <div className="form-group">
                <label>Mobile</label>
                <input
  type="text"
  value={editForm.mobile_number}
  onChange={(e) => {
    const value = e.target.value
      .replace(/\D/g, '')
      .slice(0, 10);

    setEditForm({
      ...editForm,
      mobile_number: value
    });

    setFormErrors({
      ...formErrors,
      mobile_number: ''
    });
  }}
/>
                {formErrors.mobile_number && (
                  <span className="form-error">{formErrors.mobile_number}</span>
                )}
              </div>

              <div className="modal-actions">
                <button
                  className="cancel-btn"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="save-btn"
                  onClick={handleEditAgent}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Offer Letter Modal */}
      {showOfferLetterModal && (
        <OfferLetterModal
          setDone={setDone}
          done={done}
          advisor={agent}
          onClose={() => setShowOfferLetterModal(false)}
          show={showOfferLetterModal}
        />
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default AgentDetails;